import useStore from "../store.ts";
import {Dynamic} from "../channel/DynamicDataParser.ts";
import {WrapperSubscription} from "../channel/WrapperSubscription.ts";
import {formatSource, getParent, parseEffects, parsePx} from "./ElementUtils.ts";
import {sanitizeHtml, TEXT_MARKUP_TAGS} from "./FormatUtils.ts";
import { Component} from "../types.ts";
import { enqueueRendererSrc, postUpdateGeometry} from "../worker/RenderWorkerManager.ts";

// see docs/EXTRACTED_COMMENTS.md#c176
const splitTopLevelComma = (value: string): [string, string] | null => {
    let depth = 0;
    for (let i = 0; i < value.length; i++) {
        const ch = value[i];
        if (ch === "(") depth++;
        else if (ch === ")") depth--;
        else if (ch === "," && depth === 0) {
            return [value.slice(0, i).trim(), value.slice(i + 1).trim()];
        }
    }
    return null;
};

// see docs/EXTRACTED_COMMENTS.md#c177
const balanceParens = (value: string): string => {
    let depth = 0;
    for (const ch of value) {
        if (ch === "(") depth++;
        else if (ch === ")") depth--;
    }
    return depth > 0 ? value + ")".repeat(depth) : value;
};

export const buildElements = (
    data: any,
    key: any,
    prefix = "",
    is_datasheet = false,
    deferSubscriptions = false,
): Component | null => {
    if (!data[key]) {
        return null;
    }
    if (!data[key].element) {
        return null;
    }
    const store = useStore()
    // see docs/EXTRACTED_COMMENTS.md#c178
    const item = data[key].element;
    const id = prefix != "" ? `${prefix}.${item.id}` : item.id;
    if (!id.includes('.')) {
        try {
            store.ratio = parsePx(item?.style?.width ?? "") / parsePx(item?.style?.height ?? "");
        } catch (e) {
        }
    }

    let is_dynamic = false;


    let is_new_datasheet = is_datasheet || item.object_type == "DataObjectDatasheet";
    const isTemplateContainer = item.object_type == "DataObjectContainer";

    const children = Object.keys(data[key])
        .filter(subKey => subKey !== 'element')
        .map(subKey => buildElements(data[key], subKey, "", is_new_datasheet, deferSubscriptions))
        .filter(child => child !== null);

    if (id == null) return null;
    if (item.dynamic.is_dynamic) {
        is_dynamic = true;
    }

    if (item.object_type == "DataObjectDatasheet") {
        store.datasheets.push(id);
    }

    // see docs/EXTRACTED_COMMENTS.md#c563
    const style: any = Object.assign({}, item.style);

    let scale = 1;
    // see docs/EXTRACTED_COMMENTS.md#c564
    if (id.split('.').length <= 1) {
        const width = parseFloat(style.width);
        const height = parseFloat(style.height);
        const innerScale = Math.min(innerWidth / width, innerHeight / height);
        style.transformOrigin = 'left top';
        style.transform = `scale(${innerScale * 100}%`;
        scale = innerScale
    }
    // see docs/EXTRACTED_COMMENTS.md#c565


    if (!(item.auto_size ?? "").includes("WIDTH")) {
        if (style["border-left-width"] && style["border-color"] != "transparent") {
            style.width = `${parsePx(style.width) - parsePx(style["border-left-width"])}px`;
        }

        if (style["border-right-width"] && style["border-color"] != "transparent") {
            style.width = `${parsePx(style.width) - parsePx(style["border-right-width"])}px`;
        }
    }

    if (!(item.auto_size ?? "").includes("HEIGHT")) {
        if (style["border-top-width"] && style["border-color"] != "transparent") {
            style.height = `${parsePx(style.height) - parsePx(style["border-top-width"])}px`;
        }

        if (style["border-bottom-width"] && style["border-color"] != "transparent") {
            style.height = `${parsePx(style.height) - parsePx(style["border-bottom-width"])}px`;
        }
    }

    // see docs/EXTRACTED_COMMENTS.md#c566
    for (const el of style?.css_style?.split(';') || []) {
        let arr = el.split(":");
        if (arr.length < 2) continue;
        style[arr[0].trim()] = arr[1].trim();
    }
    style.css_style = undefined;

    // see docs/EXTRACTED_COMMENTS.md#c567
    const radiusVals = String(style["border-radius"] ?? "").match(/[\d.]+/g) || [];
    const isRounded = radiusVals.some(v => parseFloat(v) > 0);
    const hasBorderlessSide = [
        style["border-top-width"], style["border-right-width"],
        style["border-bottom-width"], style["border-left-width"],
    ].some(w => parsePx(w) === 0);

    const isGradientBorderCell =
        style["border-color"] === "transparent" &&
        typeof style["background-clip"] === "string" &&
        style["background-clip"].replace(/\s+/g, "") === "padding-box,border-box";

    let isGradientBorder = false;
    if (isGradientBorderCell) {
        // (1) Geometry compensation — all gradient-border cells.
        const bl = parsePx(style["border-left-width"]);
        const br = parsePx(style["border-right-width"]);
        const bt = parsePx(style["border-top-width"]);
        const bb = parsePx(style["border-bottom-width"]);
        const w = parsePx(style.width);
        const h = parsePx(style.height);
        if (w > 0 && w - bl - br >= 0) style.width = `${w - bl - br}px`;
        if (h > 0 && h - bt - bb >= 0) style.height = `${h - bt - bb}px`;

        // see docs/EXTRACTED_COMMENTS.md#c568
        if (!isRounded && hasBorderlessSide && typeof style["background-image"] === "string") {
            const layers = splitTopLevelComma(style["background-image"]);
            if (layers) {
                isGradientBorder = true;
                const [body, gradient] = layers;
                style["--gb-body"] = balanceParens(body);
                style["--gb-gradient"] = balanceParens(gradient);
            }
        }
    }

    // see docs/EXTRACTED_COMMENTS.md#c569
    const alternateHidden = data?.element?.object_type === "DataObjectAlternating";

    // see docs/EXTRACTED_COMMENTS.md#c570
    let effectiveTransform = store.clone.transform;
    const containerTransforms = store.clone.containerTransforms;
    if (containerTransforms && containerTransforms.length) {
        let bestLen = -1;
        for (const t of containerTransforms) {
            const cid = t.container_id;
            if (!cid) continue;
            if ((id === cid || id.startsWith(cid + ".")) && cid.length > bestLen) {
                effectiveTransform = t;
                bestLen = cid.length;
            }
        }
    }

    let component: Component = {
        type: item.object_type,
        object_name: item.object_name,
        dynamic: new Dynamic(item.dynamic, effectiveTransform),
        sizing: item.auto_size,
        anchor: item.anchor,
        props: {
            id: id,
            style: Object.assign({}, style),
            key: id,
            ...(isGradientBorder ? { class: "gb-ring" } : {}),
        },
        published: {
            src: "",
            hide_flags: {
                data: ((item.auto_hide || "NO_HIDE") != "NO_HIDE") && !((item.auto_hide || "NO_HIDE").includes("CHILDREN")),
                rule: false,
                render: false,
                alternating: alternateHidden,
                image: false,
                anchoring: false,
                dedup: false,
                playlist: false,
                countdown: false,
                timer: false,
                children: false,
            },
        },
        flags: {
            updated_data: false,
            // Renderer-owned; keep main-thread default false.
            updated_position: false,
            geometry_settled: false,
            calculated_row: item.object_type != "DataObjectDatasheet",
            is_datasheet,
            template_resolved: !isTemplateContainer,
            is_cluster_sheet: false,
        },
        auto_size: item.auto_size,
        children,
        hide_flags: {
            data: ((item.auto_hide || "NO_HIDE") != "NO_HIDE") && !((item.auto_hide || "NO_HIDE").includes("CHILDREN")),
            rule: !!item.decision_props?.rule,
            render: false,
            alternating: alternateHidden,
            image: false,
            anchoring: false,
            dedup: false,
            playlist: false,
            countdown: false,
            timer: false,
            children: false,
        },
        data: {},
        datasheet_colors: item.datasheet_colors ?? undefined,
        is_dynamic: is_dynamic,
        style: style,
        scale: scale,
        auto_hide: item.auto_hide || "NO_HIDE",
        overlay: item.overlay === true,
        viewType: item.view_type,
        adoptSubscriptions: {},
        adoptData: new Map(),
        subscription: new WrapperSubscription(id),
        parent: String(getParent(id) || Object.keys(store.layoutData)[0].replace("_", "")),
        text_props: item.text_props,
        image_props: item.image_props,
        container_props: item.container_props,
        playlist_props: item.playlist_props,
        decision_props: item.decision_props,
        progress_bar_props: item.progress_bar_props,
        countdown_props: item.countdown_props,
        function_props: item.function_props,
        digital_clock_props: item.digital_clock_props,
        analog_clock_props: item.analog_clock_props,
        timer_props: item.timer_props,
        input_text_props: item.input_text_props,
        button_props: item.button_props,
        ticker_props: item.ticker_props,
        editor_props: item.editor_props,
        html_props: item.html_props,
        alternating_props: item.frame_props?.alternating,
        submit_button_props: item.submit_button_props,
        effects: item?.frame_props?.effects == null ? undefined : parseEffects(item.frame_props.effects)
    }
    if (item.text_props) {
        // see docs/EXTRACTED_COMMENTS.md#c571
        component.published.src = sanitizeHtml(item.text_props.format ?? "", TEXT_MARKUP_TAGS);
    }else if (item.image_props) {
        component.published.src = item.image_props.format;
    }else if (item.playlist_props) {
        component.published.src = item.playlist_props.format;
    }else if (item.html_props) {
        component.published.src = item.html_props.format ?? "";
    }

    if (item.text_props) {
        item.text_props.x_indent = item.text_props.x_indent ? `${item.text_props.x_indent}px` : null;
        item.text_props.y_indent = item.text_props.y_indent ? `${item.text_props.y_indent}px` : null;
    }

    if (item.dynamic.field && (component?.published?.src || "").includes("%")) {
        component.published.src = "";
    }


    // see docs/EXTRACTED_COMMENTS.md#c572
    if (item.description == "OMIT_DUPLICATE") {
        component.dynamic.original_object.omit_duplicate = true;
    }

    store.components[id] = component;
    if (!deferSubscriptions) {
        component.subscription.load();
    }

    component.published.src = formatSource(component.published.src, id);
    enqueueRendererSrc(component, component.published.src ?? "");
    postUpdateGeometry(id, "element-build");
    return component;
}

/* see docs/EXTRACTED_COMMENTS.md#c776 */
let subscriptionsDeferred = false;

export function setSubscriptionsDeferred(deferred: boolean): void {
    subscriptionsDeferred = deferred;
}

export function areSubscriptionsDeferred(): boolean {
    return subscriptionsDeferred;
}

export function activateAllSubscriptions(): void {
    const store = useStore();
    subscriptionsDeferred = false;
    for (const component of Object.values(store.components) as Component[]) {
        if (component.subscription && component.props?.id) {
            component.subscription.load();
        }
    }
}


